import React, { useContext, useEffect, useMemo, useState } from 'react'
import ky from 'ky'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight, faClockRotateLeft, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { GlobalContext } from '../App'
import { API_URL } from '../Globals'

interface AuditChange {
  field: string
  before?: unknown
  after?: unknown
}

interface AuditEntry {
  _id: string
  createdAt: string
  actor: { username: string, firstName?: string, lastName?: string }
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'MERGE' | 'RESTORE'
  entityType: 'ADDRESS' | 'USER' | 'TASK'
  entityId: string
  summary: string
  changes: AuditChange[]
}

const actionLabels = { CREATE: 'Luotu', UPDATE: 'Muokattu', DELETE: 'Poistettu', MERGE: 'Yhdistetty', RESTORE: 'Palautettu' }
const entityLabels = { ADDRESS: 'Kohde', USER: 'Käyttäjä', TASK: 'Tehtävä' }

const valueText = (value: unknown): string => {
  if (value === undefined) return '—'
  if (value === null) return 'Tyhjä'
  if (typeof value === 'string') return value || 'Tyhjä'
  return JSON.stringify(value)
}

const AuditLog: React.FC = () => {
  const { globalVariable } = useContext(GlobalContext)
  const [logs, setLogs] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [error, setError] = useState('')
  const [actionFilter, setActionFilter] = useState('ALL')
  const [entityFilter, setEntityFilter] = useState('ALL')
  const perPage = 20

  useEffect(() => {
    setLoading(true)
    setError('')
    ky.get(`${API_URL}/api/audit`, {
      searchParams: { page, perPage },
      headers: {
        Authorization: `Bearer ${globalVariable.sessionToken || ''}`,
        Token: localStorage.getItem('token') || ''
      }
    }).json<{ logs: AuditEntry[], totalCount: number }>()
      .then(data => {
        setLogs(data.logs)
        setTotalCount(data.totalCount)
      })
      .catch(() => setError('Tapahtumia ei voitu ladata. Tarkista käyttöoikeus ja palvelinyhteys.'))
      .finally(() => setLoading(false))
  }, [globalVariable.sessionToken, page])

  const visibleLogs = useMemo(() => logs.filter(entry => (
    (actionFilter === 'ALL' || entry.action === actionFilter) &&
    (entityFilter === 'ALL' || entry.entityType === entityFilter)
  )), [logs, actionFilter, entityFilter])

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  return (
    <main className="container mx-auto mb-10 px-3 sm:px-6 lg:px-8">
      <header className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tapahtumat</h1>
            <p className="mt-1 text-sm text-gray-500">Vain lokitusoikeuden saaneet ylläpitäjät näkevät nämä tiedot.</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm font-semibold text-gray-700">
            Toiminto
            <select value={actionFilter} onChange={event => setActionFilter(event.target.value)} className="mt-1 h-11 w-full rounded-xl border-gray-300 bg-white px-3 font-normal">
              <option value="ALL">Kaikki toiminnot</option>
              <option value="CREATE">Luodut</option>
              <option value="UPDATE">Muokatut</option>
              <option value="DELETE">Poistetut</option>
              <option value="MERGE">Yhdistetyt</option>
              <option value="RESTORE">Palautetut</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-gray-700">
            Kohdetyyppi
            <select value={entityFilter} onChange={event => setEntityFilter(event.target.value)} className="mt-1 h-11 w-full rounded-xl border-gray-300 bg-white px-3 font-normal">
              <option value="ALL">Kaikki kohdetyypit</option>
              <option value="ADDRESS">Kohteet</option>
              <option value="USER">Käyttäjät</option>
              <option value="TASK">Tehtävät</option>
            </select>
          </label>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-blue-600"><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan lokia…</div>
      ) : error ? (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center font-medium text-red-700">{error}</div>
      ) : visibleLogs.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500">Ei lokitapahtumia näillä rajauksilla.</div>
      ) : (
        <div className="space-y-3">
          {visibleLogs.map(entry => {
            const actorName = [entry.actor.firstName, entry.actor.lastName].filter(Boolean).join(' ')
            const actionColor = entry.action === 'DELETE' ? 'bg-red-100 text-red-700' : entry.action === 'CREATE' || entry.action === 'RESTORE' ? 'bg-emerald-100 text-emerald-700' : entry.action === 'MERGE' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
            return (
              <article key={entry._id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${actionColor}`}>{actionLabels[entry.action]}</span>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">{entityLabels[entry.entityType]}</span>
                    </div>
                    <h2 className="mt-2 font-semibold text-gray-900">{entry.summary}</h2>
                    <p className="mt-1 text-sm text-gray-500">{actorName || entry.actor.username} ({entry.actor.username})</p>
                  </div>
                  <time className="whitespace-nowrap text-sm font-medium text-gray-600" dateTime={entry.createdAt}>
                    {new Date(entry.createdAt).toLocaleString('fi-FI')}
                  </time>
                </div>

                <details className="mt-3 border-t border-gray-100 pt-3">
                  <summary className="cursor-pointer select-none text-sm font-semibold text-blue-600">Näytä muutokset ({entry.changes.length})</summary>
                  <div className="mt-3 space-y-2">
                    {entry.changes.length === 0 ? <p className="text-sm text-gray-400">Ei kenttäkohtaisia muutoksia.</p> : entry.changes.map(change => (
                      <div key={change.field} className="grid gap-1 rounded-xl bg-gray-50 p-3 text-sm sm:grid-cols-[10rem_1fr_1fr] sm:gap-3">
                        <strong className="break-words text-gray-700">{change.field}</strong>
                        <div><span className="text-xs font-semibold uppercase text-gray-400">Ennen</span><p className="break-words text-gray-600">{valueText(change.before)}</p></div>
                        <div><span className="text-xs font-semibold uppercase text-gray-400">Jälkeen</span><p className="break-words text-gray-900">{valueText(change.after)}</p></div>
                      </div>
                    ))}
                  </div>
                </details>
              </article>
            )
          })}
        </div>
      )}

      <nav className="mt-6 flex items-center justify-center gap-2" aria-label="Lokin sivutus">
        <button onClick={() => setPage(value => Math.max(1, value - 1))} disabled={page === 1} className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600 disabled:opacity-40" title="Edellinen sivu"><FontAwesomeIcon icon={faAngleLeft} /></button>
        <span className="flex h-11 min-w-24 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-bold text-white">{page} / {totalPages}</span>
        <button onClick={() => setPage(value => Math.min(totalPages, value + 1))} disabled={page === totalPages} className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600 disabled:opacity-40" title="Seuraava sivu"><FontAwesomeIcon icon={faAngleRight} /></button>
      </nav>
    </main>
  )
}

export default AuditLog
